import type { Vehicle, VehicleDerivative } from '@/features/vehiclesearch';

/**
 * Issue #819. True when a stored vehicle document would be REJECTED by
 * quote-service's `'vehicle.date_of_first_registration' =>
 * 'required_if:vehicle.type,vehicle|date'` (StoreQuoteRequest.php:225, inherited
 * unchanged by UpdateQuoteRequest — its makeFieldsOptionalForUpdate() rewrites only
 * rules beginning `required|`).
 *
 * Scoped to `type === 'vehicle'` DELIBERATELY. A `type: derivative` document is
 * exempt from that rule and is accepted raw by the server (pinned by
 * test_update_accepts_a_raw_vehicle_derivative_payload). Widening this predicate to
 * derivatives would route a legitimate selection back to the vehicle step for a field
 * the write contract does not want — the second of the two failure modes #807
 * measured when it rejected tightening isStepValid('vehicle').
 *
 * NOT written in terms of `isVehicle()` (checkVehicleType.ts:11-18), and that is
 * load-bearing: that guard additionally requires `'current_status' in vehicle`, and a
 * VIN-only CarWeb lookup produces a `type: vehicle` object with NO current_status key
 * (VehicleSearchResource.php:149 emits null when there is no VRM;
 * vinvrmsearch.ts:198-200 and :220-222 copy it only when truthy). Such a document
 * still 422s on the date, so it must still be caught here.
 *
 * The value test is "a non-empty string", not `!value`. This reads redux-persist-
 * rehydrated and wire JSON whose runtime shape is not type-guaranteed, so anything
 * that is not a usable date string counts as missing — the same reasoning
 * quotes.ts:97-105 gives for `Number.isInteger` over `typeof === 'number'`. Note in
 * particular that `!value` would MISS `[]`, the shape SimpleXML produces for an empty
 * CarWeb element (StoreQuoteRequest.php:97-121), because an empty array is truthy.
 *
 * A non-empty string is NOT automatically a date the server will take, which is why
 * the parse check below exists. The rule mirrored here is two halves, and an earlier
 * revision of this predicate mirrored only the first: `required_if` rejects an absent
 * value, and `|date` independently rejects a present-but-unparseable one. Laravel's
 * validateDate() is `strtotime($value) !== false` AND `checkdate()` over date_parse()'s
 * components, so a stored `'0000-00-00'` — the classic legacy-MySQL zero date — is a
 * non-empty string that still 422s. Leaving it unrouted reproduces the exact failure
 * this slice exists to remove: a save rejected with no route to the form that fixes it.
 *
 * TRADE-OFF, stated because the two engines are NOT equivalent: `Date.parse` and PHP's
 * strtotime + checkdate disagree at the margins, so this predicate cannot be an exact
 * mirror. The direction of the disagreement is what matters, and the two directions are
 * not symmetric:
 *
 *   over-routing (route a document the server would have accepted) costs the user one
 *     extra step on a form they can complete, and the form re-writes the date through
 *     toDateKey() on auto-submit, so it self-heals;
 *   under-routing (leave a document the server will reject) is the bug itself — a 422
 *     on save with no field-level feedback and nowhere to go.
 *
 * So where this predicate has a choice it errs toward routing. `Date.parse` returning
 * NaN is treated as missing rather than as "unknown, assume fine".
 *
 * Two residual under-routes are known and measured, both accepted here rather than
 * closed by re-implementing date_parse()/checkdate() in TypeScript — that trade names a
 * rare shape for a class of over-routes on text-month and slash-separated formats the
 * server does accept, which cannot be enumerated as confidently:
 *
 *   '2019'       — Date.parse yields 2019-01-01; PHP's date_parse reports month/day 0,
 *                  so checkdate() fails and the server 422s.
 *   '2019-02-30' — V8's legacy parser rolls it forward to 2019-03-02; checkdate(2, 30,
 *                  2019) is false, so the server 422s.
 *
 * Neither is a shape this system writes: the frontend only ever sends toDateKey()'s
 * YYYY-MM-DD (QuoteContext.tsx), and the CarWeb path emits either a real date or null
 * (VehicleSearchResource.php:111).
 *
 * #825 must IMPORT this rather than re-implement it inside isStepValid(). See the
 * scope boundary in docs/plans/2026-08-07-quote-resume-null-registration-date.md.
 */
export const isVehicleMissingRegistrationDate = (
    vehicle: Vehicle | VehicleDerivative | null | undefined,
): boolean => {
    if (!vehicle || vehicle.type !== 'vehicle') {
        return false;
    }
    const value = vehicle.date_of_first_registration;
    if (typeof value !== 'string' || value.trim() === '') {
        return true;
    }
    // Mirrors the `|date` half of the rule. See the TRADE-OFF note above for why an
    // unparseable string is treated as missing rather than as "assume the server
    // will cope".
    return Number.isNaN(Date.parse(value));
};

/**
 * Issue #825, D2. The Redux half of the vehicle step gate. True when a stored
 * vehicle document must NOT satisfy `isStepValid('vehicle')`.
 *
 * COMPOSES `isVehicleMissingRegistrationDate` rather than re-implementing it —
 * this function's own docblock above forbids that. Every measured behaviour of
 * that predicate (the `[]` SimpleXML shape, `'0000-00-00'`, `Date.parse` NaN, the
 * documented over-route bias) comes along unchanged.
 *
 * Adds exactly one thing on top: a `type !== 'vehicle'` discriminator.
 * `isVehicleMissingRegistrationDate` deliberately returns `false` for a
 * `type: derivative` document (it is exempt from quote-service's
 * `required_if:vehicle.type,vehicle|date` write rule, StoreQuoteRequest.php:225),
 * but a raw derivative is not a FINISHED vehicle in the journey sense — nothing
 * downstream can price a plan without a registration date. The step gate is
 * therefore deliberately stricter than the write rule: block stepping, keep
 * saving (issue #825, recorded decision D1). A legacy document with no `type`
 * key at all also lands on the strict side of `!== 'vehicle'`, which matches
 * the position #856's guard takes for the same reason.
 *
 * Returns `false` for a null/absent vehicle, on purpose — "no vehicle at all" is
 * a different condition owned by the caller (`isStepValid`'s own
 * `!!selectedVehicle`, and `useQuoteLoader`'s `else if (!quote.vehicle)` branch).
 * Folding the null case in here would silently reorder #819's resume chain.
 */
export const isVehicleDocumentIncomplete = (
    vehicle: Vehicle | VehicleDerivative | null | undefined,
): boolean => {
    if (!vehicle) return false;
    if (vehicle.type !== 'vehicle') return true;
    return isVehicleMissingRegistrationDate(vehicle);
};
